import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Printer, Mail } from "lucide-react";
import { api, money } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { BallotCards } from "@/components/BallotCards";

const Row = ({ k, v, id }) => (
  <div className="flex justify-between gap-4 py-2 text-sm"><dt className="text-[#6B6157]">{k}</dt><dd className="text-right text-[#1F1B17]" data-testid={id}>{v}</dd></div>
);

export default function BookingPage() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const [b, setB] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const email = params.get("email") || "";

  useEffect(() => {
    api.get(`/bookings/${code}`, { params: { email } }).then((r) => {
      setB(r.data);
      api.get(`/events/${r.data.event_slug}`).then((e) => setVerdict(e.data.verdict?.title ? e.data.verdict : null)).catch(() => {});
    }).catch(() => setB(false));
  }, [code, email]);

  if (b === false) return (
    <div className="mx-auto max-w-xl px-8 py-32 text-center" data-testid="booking-not-found">
      <p className="text-[#6B6157]">We couldn't find that booking.</p>
      <Link to="/booking" className="mt-4 inline-block text-[#A8832F] underline">Try looking it up</Link>
    </div>
  );
  if (!b) return <Skeleton className="mx-auto my-20 h-96 max-w-xl" />;
  const cancelled = b.status === "cancelled";

  return (
    <main className={`mx-auto px-5 py-16 print:py-0 ${verdict && !cancelled ? "max-w-3xl" : "max-w-xl"}`} data-testid="booking-page">
      {params.get("new") && <p className="eyebrow reveal mb-4 text-center print:hidden">Payment received</p>}
      <div className="reveal reveal-2 ticket-edge mx-auto max-w-xl border border-[#E5DACA] bg-white p-8 sm:p-10">
        <p className="eyebrow">{cancelled ? "Cancelled" : "You're in"}</p>
        <h1 className="mt-3 font-display text-3xl text-[#1F1B17]">{b.event_title}</h1>
        <p className="mt-6 font-mono text-3xl tracking-[0.3em] text-[#A8832F]" data-testid="booking-code">{b.code}</p>
        <dl className="mt-6 divide-y divide-[#E5DACA] border-y border-[#E5DACA]">
          <Row k="Guest" v={b.name} id="booking-name" />
          <Row k="Night" v={`${b.night_label ? b.night_label + " · " : ""}${b.night_date_display}${b.night_time ? " · " + b.night_time : ""}`} id="booking-night" />
          <Row k="Seats" v={`${b.quantity} × ${b.ticket_name}`} id="booking-seats" />
          <Row k="Location" v={b.location} id="booking-location" />
          <Row k="Total paid" v={money(b.total)} id="booking-total" />
          <Row k="Status" v={b.status} id="booking-status" />
          {b.refund_status && <Row k="Refund" v={`${money(b.refunded_amount)} · ${b.refund_status.toLowerCase()}`} id="booking-refund" />}
        </dl>
        <p className="mt-6 flex items-start gap-2 text-sm text-[#6B6157]"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#A8832F]" />{cancelled
          ? `This booking was cancelled${b.refund_status ? ` and ${money(b.refunded_amount)} was refunded to your card. Refunds can take 5–10 business days to appear.` : "."}`
          : `A confirmation was sent to ${b.email}. Every guest must be 21+ with a valid photo ID.`}</p>
      </div>
      {verdict && !cancelled && <BallotCards b={b} verdict={verdict} />}
      <div className="mt-8 flex flex-wrap justify-center gap-4 print:hidden">
        <button type="button" onClick={() => window.print()} data-testid="print-booking-button" className="inline-flex items-center gap-2 border border-[#1F1B17] px-5 py-3 text-xs uppercase tracking-widest text-[#A8832F] hover:bg-[#F1E9DA]"><Printer className="h-4 w-4" />{verdict && !cancelled ? "Print ticket & ballots" : "Print"}</button>
        {b.receipt_url && <a href={b.receipt_url} target="_blank" rel="noreferrer" data-testid="receipt-link" className="border border-[#1F1B17] px-5 py-3 text-xs uppercase tracking-widest text-[#A8832F] hover:bg-[#F1E9DA]">Square receipt</a>}
        <Link to="/" data-testid="back-to-events-link" className="px-5 py-3 text-xs uppercase tracking-widest text-[#6B6157] hover:text-[#1F1B17]">More events</Link>
      </div>
    </main>
  );
}
